import { auditUrlKey, type AuditTriage } from "../../../shared/aeoAuditTriage";
import DashboardLayout from "@/components/DashboardLayout";
import KeywordCreateDialog, {
  normalizeKeywordCategory,
  type KeywordBrandKey,
} from "@/components/keywords/KeywordCreateDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trpc } from "@/lib/trpc";
import { auditActionItems } from "@/lib/aeoAuditCopy";
import {
  取得優先處理問題,
  取得後續補強問題,
  取得處理優先度,
  取得主要問題,
} from "@/lib/aiCitationOperations";
import {
  AlertCircle,
  ExternalLink,
  Loader2,
  RefreshCw,
  Search,
  Target,
} from "lucide-react";
import { useEffect, useMemo, useState, useRef } from "react";
import { toast } from "sonner";

type AuditStatus = "PASS" | "WARNING" | "FAIL";
type RiskLevel = "低" | "中" | "高";
type AuditBrandFilter = "ALL" | "YMSWOOD" | "UNKNOWN";

const AUDIT_BRAND_LABELS: Record<AuditBrandFilter, string> = {
  ALL: "全部品牌",
  YMSWOOD: "原木香",
  UNKNOWN: "未分類",
};

interface AnalysisDimension {
  score: number;
  level: "良好" | "注意" | "風險";
  findings: string[];
  evidence: string[];
}

interface LegacyAuditResult {
  triage?: never;
  url: string;
  normalizedUrl: string;
  pageClassification?:
    | "首頁"
    | "分類頁"
    | "內容頁"
    | "產品頁"
    | "案例頁"
    | "品牌頁"
    | "產品分類頁"
    | "評價頁";
  meta: {
    title: string;
    h1: string;
  };
  scores: {
    overall: number;
    indexingReadiness: number;
    citationReadiness: number;
    intentCoverage: number;
    aiVisibility: number;
  };
  verdicts: {
    indexingRisk: RiskLevel;
    citationReadiness: RiskLevel;
    trackingCoverage: RiskLevel;
  };
  checks: {
    structure: AnalysisDimension & {
      longParagraphsCount: number;
      menuHeadingCount: number;
      hasSummaryLead: boolean;
      hasNoindex: boolean;
      hasCanonical: boolean;
    };
    clarity: AnalysisDimension;
    trust: AnalysisDimension;
    consistency: AnalysisDimension;
    extractability: AnalysisDimension;
    intentFit: AnalysisDimension;
    trackingCoverage: AnalysisDimension;
    aiPerformance: AnalysisDimension;
    metrics: {
      wordCount: number;
      paragraphCount: number;
      headingCount: number;
      linkCount: number;
      faqCount: number;
      schemaTypes: string[];
    };
    performance: {
      ttfb: number | null;
    };
  };
  intentCoverage: {
    inferredIntents: Array<{
      intentKeyword: string;
      intentType: string;
      confidence: number;
      reason: string;
    }>;
    trackedMatches: Array<{
      intentKeyword: string;
      matchedKeyword: string;
      matchType: string;
      trackAiOverview: boolean;
      llmTotalChecks: number;
      aioCitations30d: number;
      llmCitations30d: number;
      brandMentions30d: number;
      brandLinks30d: number;
      lastSeen: string | null;
    }>;
    untrackedIntents: Array<{
      intentKeyword: string;
      confidence: number;
      suggestedPrimaryIntent: string;
      suggestedCategory: string | null;
      reason: string;
    }>;
    pageEvidence: {
      citationCount30d: number;
      brandMentions30d: number;
      brandLinks30d: number;
      providers: string[];
      matchedCitationKeywords: string[];
      topCitedUrlForMatchedIntents: string[];
      lastSeen: string | null;
    } | null;
  };
  recommendations: {
    mustFix: string[];
    shouldFix: string[];
    unnecessary: string[];
  };
  summary: {
    headline: string;
    keyFindings: string[];
  };
  status: AuditStatus;
}

type AuditResult =
  | LegacyAuditResult
  | {
      triage: AuditTriage;
      url: string;
      normalizedUrl: string;
      meta: { title: string; h1: string };
      pageClassification?: LegacyAuditResult["pageClassification"];
      intentCoverage?: never;
      summary?: never;
      recommendations?: never;
      scores?: never;
      checks?: never;
      verdicts?: never;
      status?: AuditStatus;
    };

interface AuditData {
  generatedAt: string;
  criteria?: string;
  templateIssues?: Array<{
    key: string;
    label: string;
    action: string;
    pages: number;
  }>;
  total: number;
  passed: number;
  failed: number;
  warnings: number;
  results: AuditResult[];
}

function truncateMiddle(value: string, maxLength = 56) {
  if (value.length <= maxLength) return value;
  const head = Math.ceil((maxLength - 1) / 2);
  const tail = Math.floor((maxLength - 1) / 2);
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

function compactUrlDisplay(url: string) {
  try {
    const parsed = new URL(url);
    const path =
      parsed.pathname === "/" ? "" : truncateMiddle(parsed.pathname, 42);
    return `${parsed.host}${path}`;
  } catch {
    return truncateMiddle(url, 52);
  }
}

function normalizeUrlKey(url: string) {
  try {
    const parsed = new URL(url);
    parsed.hostname = parsed.hostname
      .toLowerCase()
      .replace(/\.$/, "")
      .replace(/^www\./, "");
    parsed.hash = "";
    [
      "srsltid",
      "fbclid",
      "gclid",
      "gbraid",
      "wbraid",
      "mc_cid",
      "mc_eid",
    ].forEach(key => parsed.searchParams.delete(key));
    Array.from(parsed.searchParams.keys()).forEach(key => {
      if (key.toLowerCase().startsWith("utm_")) {
        parsed.searchParams.delete(key);
      }
    });
    return parsed.toString().replace(/\/+$/, "");
  } catch {
    return url.trim().replace(/\/+$/, "");
  }
}

function getAuditBrand(result: AuditResult): Exclude<AuditBrandFilter, "ALL"> {
  try {
    const host = new URL(result.url).hostname
      .replace(/^www\./, "")
      .toLowerCase();
    if (host === "ymswood.com.tw") return "YMSWOOD";
  } catch {
    const haystack =
      `${result.url} ${result.meta.title} ${result.meta.h1}`.toLowerCase();
    if (haystack.includes("ymswood.com.tw") || haystack.includes("原木香"))
      return "YMSWOOD";
  }
  return "UNKNOWN";
}

function 引用結果Badge(hasAiPerformance: boolean | null) {
  return (
    <Badge
      className={hasAiPerformance ? "bg-sky-700" : ""}
      variant={hasAiPerformance ? "default" : "secondary"}
    >
      {hasAiPerformance === null
        ? "引用資料未取得"
        : hasAiPerformance
          ? "有引用紀錄"
          : "未查得引用紀錄"}
    </Badge>
  );
}

function 處理優先度Badge(priority: string) {
  const className =
    priority === "優先處理"
      ? "border-red-200 bg-red-50 text-red-700"
      : priority === "後續補強"
        ? "border-amber-200 bg-amber-50 text-amber-700"
        : "border-emerald-200 bg-emerald-50 text-emerald-700";

  return (
    <Badge variant="outline" className={className}>
      {priority}
    </Badge>
  );
}

function auditPriority(row: AuditResult) {
  return row.triage?.priority || 取得處理優先度(row);
}
function auditProblem(row: AuditResult) {
  return row.triage
    ? auditActionItems(row.triage).join("\n")
    : 取得主要問題(row);
}
export default function AEOAudit() {
  const [data, setData] = useState<AuditData | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [brandFilter, setBrandFilter] = useState<AuditBrandFilter>("YMSWOOD");
  const [filterStatus, setFilterStatus] = useState<
    "ALL" | "URGENT" | "FOLLOWUP" | "CAMPAIGN" | "ERROR"
  >("URGENT");
  const [citationFilter, setCitationFilter] = useState<"ALL" | "CITED">("ALL");
  const [selectedResult, setSelectedResult] = useState<AuditResult | null>(
    null
  );
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailHydrated, setDetailHydrated] = useState(false);
  const [trackingCandidate, setTrackingCandidate] = useState<{
    keyword: string;
    category: string | null;
    brand: KeywordBrandKey;
  } | null>(null);

  const fullAudit = trpc.audit.fullReport.useQuery(undefined, {
    refetchInterval: query =>
      query.state.data?.run.status === "running" ? 5000 : false,
  });
  const advancing = useRef(false);
  const advanceAudit = trpc.audit.advanceFull.useMutation({
    onSuccess: r => {
      advancing.current = false;
      if (r.progressed) fullAudit.refetch();
    },
    onError: e => {
      advancing.current = false;
      toast.error(e.message);
    },
  });
  useEffect(() => {
    if (fullAudit.data?.run.status === "running" && !advancing.current) {
      advancing.current = true;
      advanceAudit.mutate();
    }
  }, [fullAudit.dataUpdatedAt, fullAudit.data?.run.status]);
  const startAudit = trpc.audit.startFull.useMutation({
    onSuccess: () => {
      fullAudit.refetch();
    },
    onError: e => toast.error(e.message),
  });
  useEffect(() => {
    if (fullAudit.isLoading) return;
    setData((fullAudit.data?.report as AuditData) || null);
    setLoading(false);
  }, [fullAudit.data, fullAudit.isLoading]);
  const checkPageMutation = trpc.audit.checkPage.useMutation();
  const aiPerformancePagesQuery =
    trpc.dashboard.getAiPerformancePageIndex.useQuery({
      days: 30,
    });

  const aiPerformancePageIndex = useMemo(() => {
    const map = new Map<
      string,
      { citations: number; providers: string[]; keywords: string[] }
    >();
    for (const item of aiPerformancePagesQuery.data?.items || []) {
      map.set(normalizeUrlKey(item.url), {
        citations: item.citations,
        providers: item.providers || [],
        keywords: item.keywords || [],
      });
    }
    return map;
  }, [aiPerformancePagesQuery.data]);

  const brandCounts = useMemo(() => {
    const counts: Record<AuditBrandFilter, number> = {
      ALL: data?.results.length || 0,
      YMSWOOD: 0,
      UNKNOWN: 0,
    };
    for (const result of data?.results || []) {
      counts[getAuditBrand(result)] += 1;
    }
    return counts;
  }, [data]);

  const brandResults = useMemo(() => {
    if (!data) return [];
    return data.results.filter(
      result => brandFilter === "ALL" || getAuditBrand(result) === brandFilter
    );
  }, [brandFilter, data]);

  const brandSummary = useMemo(() => {
    const citedPages = new Set(
      brandResults
        .filter(item => aiPerformancePageIndex.has(normalizeUrlKey(item.url)))
        .map(item => auditUrlKey(item.triage?.canonical || item.normalizedUrl))
    ).size;
    return {
      total: brandResults.length,
      citedPages,
      urgent: brandResults.filter(item => auditPriority(item) === "優先處理")
        .length,
      followup: brandResults.filter(item => auditPriority(item) === "後續補強")
        .length,
    };
  }, [aiPerformancePageIndex, brandResults]);

  const filteredResults = useMemo(() => {
    return brandResults.filter(result => {
      const haystack =
        `${result.url} ${result.meta.title} ${result.meta.h1}`.toLowerCase();
      const matchesSearch = haystack.includes(searchTerm.toLowerCase());
      const matchesCitation =
        citationFilter === "ALL" ||
        aiPerformancePageIndex.has(normalizeUrlKey(result.url));
      const priority = auditPriority(result);
      const matchesStatus =
        filterStatus === "ALL" ||
        (filterStatus === "URGENT" && priority === "優先處理") ||
        (filterStatus === "FOLLOWUP" && priority === "後續補強") ||
        (filterStatus === "CAMPAIGN" && priority === "活動另列") ||
        (filterStatus === "ERROR" && priority === "待查證");
      return matchesSearch && matchesCitation && matchesStatus;
    });
  }, [
    aiPerformancePageIndex,
    brandResults,
    citationFilter,
    filterStatus,
    searchTerm,
  ]);

  const resetResultFilters = () => {
    setCitationFilter("ALL");
    setFilterStatus("ALL");
  };

  const hydrateDetail = async (result: AuditResult) => {
    setSelectedResult(result);
    setDetailLoading(true);
    setDetailHydrated(false);
    if (result.triage) {
      setDetailLoading(false);
      setDetailHydrated(true);
      return;
    }

    try {
      const res = await checkPageMutation.mutateAsync({ url: result.url });
      if (!res.success || !res.result) {
        setDetailHydrated(false);
        toast.error("無法載入最新健檢結果，系統已記錄");
        return;
      }

      const updated = res.result as AuditResult;
      setSelectedResult(updated);
      setDetailHydrated(true);
      setData(prev => {
        if (!prev) return prev;
        const results = prev.results.map(item =>
          item.url === updated.url ? updated : item
        );
        return {
          ...prev,
          results,
          total: results.length,
          passed: results.filter(item => item.status === "PASS").length,
          failed: results.filter(item => item.status === "FAIL").length,
          warnings: results.filter(item => item.status === "WARNING").length,
        };
      });
    } catch {
      setDetailHydrated(false);
      toast.error("無法載入最新健檢結果，系統已記錄");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleOpenDetail = (result: AuditResult) => {
    void hydrateDetail(result);
  };

  const handleRecheck = async () => {
    if (!selectedResult) return;
    await hydrateDetail(selectedResult);
  };

  const detailIntents = selectedResult?.intentCoverage
    ? selectedResult.intentCoverage.inferredIntents.map(intent => {
        const trackedMatch = selectedResult.intentCoverage.trackedMatches.find(
          match => match.intentKeyword === intent.intentKeyword
        );
        const untrackedIntent =
          selectedResult.intentCoverage.untrackedIntents.find(
            item => item.intentKeyword === intent.intentKeyword
          );

        return {
          intentKeyword: intent.intentKeyword,
          trackedMatch,
          untrackedIntent,
        };
      })
    : [];
  const urgentIssueCards = 取得優先處理問題(selectedResult);
  const followupIssueCards = 取得後續補強問題(selectedResult);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8">載入中...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container py-8 space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-2">AEO 全站健檢</h1>
            <p className="text-muted-foreground">
              最後更新：
              {data
                ? new Date(data.generatedAt).toLocaleString("zh-TW", {
                    timeZone: "Asia/Taipei",
                  })
                : "-"}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              以收錄準備度、內容一致性、意圖覆蓋與既有 AI 表現為主，不再用缺少
              Schema 當主判準。
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              disabled={
                startAudit.isPending || fullAudit.data?.run.status === "running"
              }
              onClick={() => startAudit.mutate()}
            >
              {startAudit.isPending ? "正在啟動" : "正式重跑全站"}
            </Button>
            <Button variant="outline" onClick={() => window.location.reload()}>
              重新整理
            </Button>
          </div>
        </div>

        {fullAudit.error ? (
          <p role="alert" className="text-red-700">
            正式健檢讀取失敗：{fullAudit.error.message}
          </p>
        ) : null}
        {!fullAudit.data && !fullAudit.isLoading && !fullAudit.error ? (
          <p>目前尚無正式健檢結果，請啟動正式重跑</p>
        ) : null}
        {fullAudit.data?.run.status === "running" ? (
          <p role="status">
            正式健檢進行中：{fullAudit.data.run.processed}/
            {fullAudit.data.run.total}，請保持此頁開啟；重新開啟會接續未完成頁面
          </p>
        ) : null}
        {data?.criteria ? (
          <p className="text-sm text-muted-foreground">{data.criteria}</p>
        ) : null}
        <p className="text-sm text-muted-foreground">
          FAQ、摘要、更新日期等通用提醒不單獨列為急件；共用模板問題集中處理。前20名是本輪工作順序
        </p>
        {data?.templateIssues?.length ? (
          <details className="rounded border p-4">
            <summary className="cursor-pointer font-medium">
              共用模板檢查
            </summary>
            <ul className="mt-2 space-y-2 text-sm">
              {data.templateIssues.map(item => (
                <li key={item.key}>
                  {item.label}：{item.pages} 頁；{item.action}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
        {fullAudit.data && fullAudit.data.run.status !== "running" ? (
          <p>
            本輪狀態：
            {fullAudit.data.run.status === "completed"
              ? "全站檢查完成"
              : fullAudit.data.run.status === "partial_success"
                ? "部分頁面檢查失敗"
                : "作業失敗"}
            ，已檢查 {fullAudit.data.run.processed}/{fullAudit.data.run.total}
            ，失敗 {fullAudit.data.run.errors} 頁
          </p>
        ) : null}
        {data ? (
          <>
            <Tabs
              value={brandFilter}
              onValueChange={value => setBrandFilter(value as AuditBrandFilter)}
            >
              <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 h-auto gap-2 sm:gap-0">
                {(["YMSWOOD", "ALL", "UNKNOWN"] as AuditBrandFilter[]).map(
                  brand => (
                    <TabsTrigger key={brand} value={brand} className="gap-2">
                      <span>{AUDIT_BRAND_LABELS[brand]}</span>
                      <Badge variant="secondary">{brandCounts[brand]}</Badge>
                    </TabsTrigger>
                  )
                )}
              </TabsList>
            </Tabs>

            <div className="grid gap-6 md:grid-cols-4">
              <Card
                className={`cursor-pointer transition-colors hover:bg-muted/40 ${
                  citationFilter === "ALL" && filterStatus === "ALL"
                    ? "ring-2 ring-primary"
                    : ""
                }`}
                role="button"
                tabIndex={0}
                onClick={resetResultFilters}
                onKeyDown={event => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    resetResultFilters();
                  }
                }}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">
                    檢查網址數
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{brandSummary.total}</div>
                </CardContent>
              </Card>
              <Card
                className={`cursor-pointer transition-colors hover:bg-muted/40 ${
                  citationFilter === "CITED" ? "ring-2 ring-sky-600" : ""
                }`}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setCitationFilter("CITED");
                  setFilterStatus("ALL");
                }}
                onKeyDown={event => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setCitationFilter("CITED");
                    setFilterStatus("ALL");
                  }
                }}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">
                    有引用紀錄的頁面
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-sky-700">
                    {aiPerformancePagesQuery.isSuccess
                      ? brandSummary.citedPages
                      : "未取得"}
                  </div>
                </CardContent>
              </Card>
              <Card
                className={`cursor-pointer transition-colors hover:bg-muted/40 ${
                  filterStatus === "URGENT" && citationFilter === "ALL"
                    ? "ring-2 ring-red-600"
                    : ""
                }`}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setFilterStatus("URGENT");
                  setCitationFilter("ALL");
                }}
                onKeyDown={event => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setFilterStatus("URGENT");
                    setCitationFilter("ALL");
                  }
                }}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">
                    優先前 20 頁
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">
                    {brandSummary.urgent}
                  </div>
                </CardContent>
              </Card>
              <Card
                className={`cursor-pointer transition-colors hover:bg-muted/40 ${
                  filterStatus === "FOLLOWUP" && citationFilter === "ALL"
                    ? "ring-2 ring-amber-600"
                    : ""
                }`}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setFilterStatus("FOLLOWUP");
                  setCitationFilter("ALL");
                }}
                onKeyDown={event => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setFilterStatus("FOLLOWUP");
                    setCitationFilter("ALL");
                  }
                }}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">
                    後續補強網址
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-amber-600">
                    {brandSummary.followup}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex gap-4 items-center">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="搜尋網址或標題..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant={
                    filterStatus === "ALL" && citationFilter === "ALL"
                      ? "default"
                      : "outline"
                  }
                  onClick={resetResultFilters}
                >
                  全部
                </Button>
                <Button
                  variant={citationFilter === "CITED" ? "default" : "outline"}
                  onClick={() => {
                    setCitationFilter("CITED");
                    setFilterStatus("ALL");
                  }}
                >
                  只看有引用
                </Button>
                <Button
                  variant={
                    filterStatus === "URGENT" && citationFilter === "ALL"
                      ? "destructive"
                      : "outline"
                  }
                  onClick={() => {
                    setFilterStatus("URGENT");
                    setCitationFilter("ALL");
                  }}
                >
                  優先前 20 頁
                </Button>
                <Button
                  variant={
                    filterStatus === "FOLLOWUP" && citationFilter === "ALL"
                      ? "secondary"
                      : "outline"
                  }
                  onClick={() => {
                    setFilterStatus("FOLLOWUP");
                    setCitationFilter("ALL");
                  }}
                >
                  只看後續補強
                </Button>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant={filterStatus === "CAMPAIGN" ? "secondary" : "outline"}
                onClick={() => {
                  setFilterStatus("CAMPAIGN");
                  setCitationFilter("ALL");
                }}
              >
                活動另列（
                {
                  brandResults.filter(x => x.triage?.priority === "活動另列")
                    .length
                }
                ）
              </Button>
              <Button
                variant={filterStatus === "ERROR" ? "secondary" : "outline"}
                onClick={() => {
                  setFilterStatus("ERROR");
                  setCitationFilter("ALL");
                }}
              >
                檢查失敗（
                {
                  brandResults.filter(x => x.triage?.priority === "待查證")
                    .length
                }
                ）
              </Button>
            </div>
            <div className="border rounded-md">
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[420px]">頁面</TableHead>
                    <TableHead className="w-[110px]">引用結果</TableHead>
                    <TableHead className="w-[130px]">處理優先度</TableHead>
                    <TableHead className="w-[320px]">目前問題</TableHead>
                    <TableHead className="w-[170px]">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredResults.map(result => {
                    const hasAiPerformance = aiPerformancePageIndex.has(
                      normalizeUrlKey(result.url)
                    );
                    const priority = auditPriority(result);
                    return (
                      <TableRow
                        key={result.url}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleOpenDetail(result)}
                      >
                        <TableCell className="align-top">
                          <Badge variant="outline" className="mb-2">
                            {AUDIT_BRAND_LABELS[getAuditBrand(result)]}
                          </Badge>
                          <div
                            className="font-medium line-clamp-2 max-w-[340px] leading-5"
                            title={result.meta.title || result.url}
                          >
                            {result.meta.title || result.url}
                          </div>
                          <div
                            className="text-xs text-muted-foreground font-mono mt-1 max-w-[340px] truncate"
                            title={result.url}
                          >
                            {compactUrlDisplay(result.url)}
                          </div>
                        </TableCell>
                        <TableCell>
                          {引用結果Badge(
                            aiPerformancePagesQuery.isSuccess
                              ? hasAiPerformance
                              : null
                          )}
                        </TableCell>
                        <TableCell className="align-top">
                          {處理優先度Badge(priority)}
                          {result.triage?.rank ? (
                            <p className="mt-1 text-sm">
                              第 {result.triage.rank} 順位
                            </p>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div
                              className="leading-5 whitespace-pre-line"
                              title={auditProblem(result)}
                            >
                              {auditProblem(result)}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell onClick={e => e.stopPropagation()}>
                          <div className="flex flex-row gap-2 items-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenDetail(result)}
                            >
                              查看詳情
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                window.location.href = `/content-optimizer?url=${encodeURIComponent(result.url)}`;
                              }}
                            >
                              文章檢查
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        ) : null}
        <Dialog
          open={!!selectedResult}
          onOpenChange={open => {
            if (!open) {
              setDetailLoading(false);
              setDetailHydrated(false);
              setSelectedResult(null);
            }
          }}
        >
          <DialogContent className="w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] sm:w-[calc(100vw-2rem)] sm:max-w-[calc(100vw-2rem)] md:max-w-3xl lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader>
              <div className="flex flex-col gap-3 pr-6 sm:flex-row sm:items-center sm:justify-between">
                <DialogTitle className="text-lg sm:text-xl">
                  健檢詳情
                </DialogTitle>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleRecheck}
                  disabled={
                    checkPageMutation.isPending ||
                    Boolean(selectedResult?.triage)
                  }
                  className="w-full sm:w-auto"
                >
                  {checkPageMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  立即重新檢測
                </Button>
              </div>
              <DialogDescription>
                <a
                  href={selectedResult?.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 hover:underline flex items-start gap-1 break-all"
                >
                  {selectedResult?.url}
                  <ExternalLink className="h-3 w-3 mt-1 shrink-0" />
                </a>
              </DialogDescription>
            </DialogHeader>

            {selectedResult && (
              <div className="space-y-6">
                {detailLoading && !detailHydrated ? (
                  <div className="rounded-lg border bg-muted/30 py-16 flex flex-col items-center justify-center gap-4">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    <div className="text-sm text-muted-foreground">
                      載入最新健檢結果中
                    </div>
                  </div>
                ) : selectedResult.triage ? (
                  <div className="space-y-4 text-sm">
                    <h2 className="text-lg font-semibold">
                      {selectedResult.meta.title}
                    </h2>
                    <p>
                      檢查時間：
                      {new Date(selectedResult.triage.checkedAt).toLocaleString(
                        "zh-TW",
                        { timeZone: "Asia/Taipei" }
                      )}
                    </p>
                    <p>
                      頁面狀態：{selectedResult.triage.lifecycle}｜
                      {auditPriority(selectedResult)}
                      {selectedResult.triage.rank
                        ? `｜第 ${selectedResult.triage.rank} 順位`
                        : ""}
                    </p>
                    <p>HTTP：{selectedResult.triage.httpStatus ?? "未取得"}</p>
                    <p className="break-all">
                      最終網址：{selectedResult.normalizedUrl}
                    </p>
                    <p className="break-all">
                      標準網址：{selectedResult.triage.canonical || "未辨識到"}
                    </p>
                    {selectedResult.triage.endDate ? (
                      <p>活動結束日期：{selectedResult.triage.endDate}</p>
                    ) : null}
                    {selectedResult.triage.lifecycleEvidence.map(item => (
                      <p key={item}>{item}</p>
                    ))}
                    <h3 className="font-semibold">排序與查證依據</h3>
                    <ul className="list-disc pl-5">
                      {selectedResult.triage.reasons.map(item => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                    <h3 className="font-semibold">處理方法</h3>
                    <ul className="list-disc pl-5">
                      {auditActionItems(selectedResult.triage).map(item => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                    {selectedResult.triage.lifecycle === "檢查失敗" ? (
                      <p>
                        此頁未取得完整健檢結果，可從「正式重跑全站」重新抓取
                      </p>
                    ) : null}
                  </div>
                ) : detailHydrated ? (
                  <>
                    <div className="grid gap-3 grid-cols-2 xl:grid-cols-5">
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">頁面類型</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <Badge variant="outline">
                            {selectedResult.pageClassification || "內容頁"}
                          </Badge>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">引用結果</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {引用結果Badge(
                            aiPerformancePagesQuery.isSuccess
                              ? aiPerformancePageIndex.has(
                                  normalizeUrlKey(selectedResult.url)
                                )
                              : null
                          )}
                        </CardContent>
                      </Card>
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">處理優先度</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {處理優先度Badge(auditPriority(selectedResult))}
                        </CardContent>
                      </Card>
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">
                            近 30 天引用次數
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-2xl font-bold">
                            {aiPerformancePageIndex.get(
                              normalizeUrlKey(selectedResult.url)
                            )?.citations ??
                              (aiPerformancePagesQuery.isSuccess
                                ? "未查得紀錄"
                                : "未取得")}
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">關聯關鍵字</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-sm">
                            {(
                              aiPerformancePageIndex.get(
                                normalizeUrlKey(selectedResult.url)
                              )?.keywords || []
                            )
                              .slice(0, 3)
                              .join("、") || "目前沒有資料"}
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    <div className="p-4 rounded-lg bg-muted/60 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {selectedResult.summary.headline}
                        </span>
                      </div>
                      {selectedResult.summary.keyFindings.length > 0 && (
                        <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1">
                          {selectedResult.summary.keyFindings.map(item => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <Tabs defaultValue="intent">
                      <TabsList className="grid w-full grid-cols-1 sm:grid-cols-2 h-auto gap-2 sm:gap-0">
                        <TabsTrigger value="intent">潛在意圖</TabsTrigger>
                        <TabsTrigger value="issues">問題與建議</TabsTrigger>
                      </TabsList>

                      <TabsContent value="intent" className="space-y-4 mt-4">
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Target className="h-4 w-4" />
                              關鍵字追蹤狀態
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="text-sm text-muted-foreground">
                              共{" "}
                              {
                                selectedResult.intentCoverage.inferredIntents
                                  .length
                              }{" "}
                              個潛在關鍵字，已追蹤{" "}
                              {
                                selectedResult.intentCoverage.trackedMatches
                                  .length
                              }{" "}
                              / 未追蹤{" "}
                              {
                                selectedResult.intentCoverage.untrackedIntents
                                  .length
                              }
                            </div>
                            {detailIntents.length === 0 ? (
                              <div className="text-sm text-muted-foreground">
                                目前沒有可顯示的關鍵字
                              </div>
                            ) : (
                              detailIntents.map(item => (
                                <div
                                  key={item.intentKeyword}
                                  className="border rounded-lg p-3 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between"
                                >
                                  <div className="min-w-0 w-full sm:w-auto">
                                    <div className="font-medium break-words">
                                      {item.intentKeyword}
                                    </div>
                                    {item.trackedMatch &&
                                    item.trackedMatch.matchedKeyword !==
                                      item.intentKeyword ? (
                                      <div className="text-xs text-muted-foreground mt-1">
                                        對應追蹤關鍵字：
                                        {item.trackedMatch.matchedKeyword}
                                      </div>
                                    ) : null}
                                  </div>
                                  <div className="shrink-0 w-full sm:w-auto">
                                    {item.trackedMatch ? (
                                      <Badge className="bg-green-600">
                                        已追蹤
                                      </Badge>
                                    ) : item.untrackedIntent ? (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="w-full sm:w-auto"
                                        onClick={() => {
                                          const candidate =
                                            item.untrackedIntent;
                                          if (!candidate) return;
                                          const brand = "YMSWOOD";
                                          setTrackingCandidate({
                                            keyword: candidate.intentKeyword,
                                            category: normalizeKeywordCategory(
                                              candidate.suggestedCategory,
                                              brand
                                            ),
                                            brand,
                                          });
                                        }}
                                      >
                                        加入關鍵字列表
                                      </Button>
                                    ) : (
                                      <Badge variant="secondary">待確認</Badge>
                                    )}
                                  </div>
                                </div>
                              ))
                            )}
                          </CardContent>
                        </Card>
                      </TabsContent>

                      <TabsContent value="issues" className="space-y-4 mt-4">
                        {urgentIssueCards.length === 0 &&
                        followupIssueCards.length === 0 ? (
                          <Card>
                            <CardHeader className="pb-2">
                              <CardTitle className="text-base">
                                目前沒有明顯的此頁問題
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="text-sm text-muted-foreground">
                              這一頁目前沒有需要特別處理的頁面級問題；全站共用模板問題不會在這裡顯示。
                            </CardContent>
                          </Card>
                        ) : (
                          <div className="space-y-6">
                            {urgentIssueCards.length > 0 && (
                              <div className="space-y-4">
                                <div className="text-base font-semibold text-red-600">
                                  優先處理
                                </div>
                                <div className="grid gap-4 lg:grid-cols-2">
                                  {urgentIssueCards.map(card => (
                                    <Card
                                      key={card.key}
                                      className="border-red-200"
                                    >
                                      <CardHeader className="pb-2">
                                        <CardTitle className="text-base text-red-600 flex items-center gap-2">
                                          <AlertCircle className="h-4 w-4" />
                                          {card.title}
                                        </CardTitle>
                                      </CardHeader>
                                      <CardContent className="space-y-4">
                                        <div>
                                          <div className="text-sm font-medium mb-2">
                                            目前問題
                                          </div>
                                          <p className="text-sm text-muted-foreground leading-6">
                                            {card.problem}
                                          </p>
                                        </div>
                                        <div>
                                          <div className="text-sm font-medium mb-2">
                                            建議怎麼修
                                          </div>
                                          <ul className="list-disc pl-5 text-sm space-y-2">
                                            {card.howToFix.map(item => (
                                              <li key={item}>{item}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      </CardContent>
                                    </Card>
                                  ))}
                                </div>
                              </div>
                            )}
                            {followupIssueCards.length > 0 && (
                              <div className="space-y-4">
                                <div className="text-base font-semibold text-amber-700">
                                  後續補強
                                </div>
                                <div className="grid gap-4 lg:grid-cols-2">
                                  {followupIssueCards.map(card => (
                                    <Card
                                      key={card.key}
                                      className="border-amber-200"
                                    >
                                      <CardHeader className="pb-2">
                                        <CardTitle className="text-base text-amber-700">
                                          {card.title}
                                        </CardTitle>
                                      </CardHeader>
                                      <CardContent className="space-y-4">
                                        <div>
                                          <div className="text-sm font-medium mb-2">
                                            目前問題
                                          </div>
                                          <p className="text-sm text-muted-foreground leading-6">
                                            {card.problem}
                                          </p>
                                        </div>
                                        <div>
                                          <div className="text-sm font-medium mb-2">
                                            建議怎麼修
                                          </div>
                                          <ul className="list-disc pl-5 text-sm space-y-2">
                                            {card.howToFix.map(item => (
                                              <li key={item}>{item}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      </CardContent>
                                    </Card>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </TabsContent>
                    </Tabs>
                  </>
                ) : (
                  <div className="rounded-lg border bg-muted/30 py-16 flex flex-col items-center justify-center gap-4">
                    <div className="text-sm text-muted-foreground">
                      最新健檢結果暫時無法載入
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleRecheck}
                      disabled={checkPageMutation.isPending}
                    >
                      {checkPageMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      再試一次
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
        <KeywordCreateDialog
          open={!!trackingCandidate}
          onOpenChange={open => {
            if (!open) setTrackingCandidate(null);
          }}
          defaultKeyword={trackingCandidate?.keyword || ""}
          defaultCategory={trackingCandidate?.category || "未分類"}
          defaultBrand={trackingCandidate?.brand}
          onCreated={() => {
            if (!selectedResult) return;
            void handleRecheck();
            setTrackingCandidate(null);
          }}
        />
      </div>
    </DashboardLayout>
  );
}
